import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Brush,
} from "recharts";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";

export interface SessionScoreData {
  id: number;
  date: Date;
  score: number | null;
  scoreMax: number | null;
  moduleName?: string;
}

interface ScoreProgressionChartProps {
  sessions: SessionScoreData[];
  showTrendLine?: boolean;
  totalSessionsCount?: number; // Total sessions count (including those without scores)
}

const ScoreProgressionChart = ({
  sessions,
  showTrendLine = true,
  totalSessionsCount,
}: ScoreProgressionChartProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  // State for brush range
  const [brushRange, setBrushRange] = useState<{ startIndex?: number; endIndex?: number }>({});

  // Track previous sessions to detect data changes
  const prevSessionsRef = useRef<SessionScoreData[]>(sessions);

  // Sort sessions by date and prepare chart data
  const chartData = useMemo(() => {
    const sortedSessions = [...sessions]
      .filter((s) => s.score !== null)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Check if data spans multiple years
    const years = new Set(sortedSessions.map((s) => s.date.getFullYear()));
    const spansMultipleYears = years.size > 1;

    return sortedSessions.map((session, index) => ({
      index: index + 1,
      date: session.date,
      // Include year if data spans multiple years
      dateLabel: spansMultipleYears
        ? format(session.date, "MMM yy", { locale })
        : format(session.date, "dd MMM", { locale }),
      fullDate: format(session.date, "dd MMM yyyy", { locale }),
      score: session.score,
      scoreMax: session.scoreMax || 20,
      moduleName: session.moduleName,
    }));
  }, [sessions, locale]);

  // Reset brush range when sessions data changes (e.g., when filters change)
  useEffect(() => {
    if (prevSessionsRef.current !== sessions) {
      setBrushRange({});
      prevSessionsRef.current = sessions;
    }
  }, [sessions]);

  // Calculate visible session count and average score (for visible range)
  const { visibleSessionCount, avgScore } = useMemo(() => {
    if (chartData.length === 0) return { visibleSessionCount: 0, avgScore: 0 };
    const startIdx = brushRange.startIndex ?? 0;
    const endIdx = brushRange.endIndex ?? chartData.length - 1;
    const visibleData = chartData.slice(startIdx, endIdx + 1);
    if (visibleData.length === 0) return { visibleSessionCount: 0, avgScore: 0 };
    const sum = visibleData.reduce((acc, d) => acc + (d.score || 0), 0);
    return {
      visibleSessionCount: visibleData.length,
      avgScore: sum / visibleData.length,
    };
  }, [chartData, brushRange]);

  // Calculate trend (simple linear regression)
  const trend = useMemo(() => {
    if (chartData.length < 2) return null;

    const n = chartData.length;
    const sumX = chartData.reduce((acc, _, i) => acc + i, 0);
    const sumY = chartData.reduce((acc, d) => acc + (d.score || 0), 0);
    const sumXY = chartData.reduce((acc, d, i) => acc + i * (d.score || 0), 0);
    const sumXX = chartData.reduce((acc, _, i) => acc + i * i, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);

    if (slope > 0.5) return "up";
    if (slope < -0.5) return "down";
    return "stable";
  }, [chartData]);

  // Determine if we should show dots based on visible data density
  const showDots = visibleSessionCount <= 50;

  // Quick range buttons
  const setQuickRange = (count: number) => {
    if (chartData.length <= count) {
      setBrushRange({
        startIndex: 0,
        endIndex: chartData.length - 1,
      });
    } else {
      setBrushRange({
        startIndex: chartData.length - count,
        endIndex: chartData.length - 1,
      });
    }
  };

  const resetZoom = () => {
    setBrushRange({
      startIndex: 0,
      endIndex: chartData.length - 1,
    });
  };

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        {t("statsNew.charts.noData")}
      </div>
    );
  }

  const getTrendIcon = () => {
    if (!trend) return null;

    if (trend === "up") {
      return (
        <div className="flex items-center gap-1 text-green-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
          <span className="text-xs font-medium">{t("statsNew.progression.improving")}</span>
        </div>
      );
    }
    if (trend === "down") {
      return (
        <div className="flex items-center gap-1 text-red-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
          <span className="text-xs font-medium">{t("statsNew.progression.declining")}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 text-gray-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
        </svg>
        <span className="text-xs font-medium">{t("statsNew.progression.stable")}</span>
      </div>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload[0]) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-gray-900">{data.fullDate}</p>
        {data.moduleName && <p className="text-xs text-gray-500 mt-1">{data.moduleName}</p>}
        <p className="text-sm mt-2">
          <span className="font-semibold text-green-600">{data.score?.toFixed(1)}</span>
          <span className="text-gray-400"> / {data.scoreMax}</span>
        </p>
      </div>
    );
  };

  // Handle brush change
  const handleBrushChange = (newRange: any) => {
    if (newRange && typeof newRange.startIndex === "number") {
      setBrushRange({
        startIndex: newRange.startIndex,
        endIndex: newRange.endIndex,
      });
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with trend indicator and quick range buttons */}
      <div className="flex-shrink-0 flex items-center justify-between mb-1">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {t("statsNew.progression.sessions")}:{" "}
            <span className="font-medium text-gray-700">{visibleSessionCount}</span>
            {totalSessionsCount && totalSessionsCount > chartData.length && (
              <span className="text-xs text-gray-400 ml-1">
                / {totalSessionsCount} ({t("statsNew.progression.withScore")})
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500">
            {t("statsNew.progression.average")}:{" "}
            <span className="font-medium text-gray-700">{avgScore.toFixed(1)}</span>
          </div>
        </div>
        {showTrendLine && getTrendIcon()}
      </div>

      {/* Quick range buttons - only show if many data points */}
      {chartData.length > 30 && (
        <div className="flex-shrink-0 flex items-center gap-2 mb-1">
          <span className="text-xs text-gray-400">{t("statsNew.progression.quickRange")}:</span>
          <button
            onClick={() => setQuickRange(30)}
            className="px-2 py-0.5 text-xs rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            30
          </button>
          <button
            onClick={() => setQuickRange(100)}
            className="px-2 py-0.5 text-xs rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            100
          </button>
          <button
            onClick={() => setQuickRange(500)}
            className="px-2 py-0.5 text-xs rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            500
          </button>
          <button
            onClick={resetZoom}
            className="px-2 py-0.5 text-xs rounded bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
          >
            {t("statsNew.progression.viewAll")}
          </button>
        </div>
      )}

      {/* Chart */}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 60, left: 60, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 10, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 20]}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Average line */}
            <ReferenceLine
              y={avgScore}
              stroke="#9ca3af"
              strokeDasharray="5 5"
              label={{
                value: t("statsNew.charts.average"),
                position: "right",
                fontSize: 10,
                fill: "#9ca3af",
              }}
            />
            {/* Score line */}
            <Line
              type="monotone"
              dataKey="score"
              stroke="#00c889"
              strokeWidth={showDots ? 2 : 1.5}
              dot={showDots ? { fill: "#00c889", strokeWidth: 2, r: 3 } : false}
              activeDot={{ fill: "#00c889", strokeWidth: 2, r: 5 }}
            />
            {/* Brush for zoom/pan - only show if many data points */}
            {chartData.length > 30 && (
              <Brush
                dataKey="dateLabel"
                height={20}
                stroke="#00c889"
                fill="#f0fdf4"
                travellerWidth={10}
                startIndex={brushRange.startIndex}
                endIndex={brushRange.endIndex}
                onChange={handleBrushChange}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ScoreProgressionChart;
